import { createSelector } from '@reduxjs/toolkit';

// A LEAF module: it reads state.AccessLogs and nothing else. The window selectors
// live apart from ./selectors so consumers can import them without its cycle.
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const selectWindows = (state) => state.AccessLogs.logsWindow;

//📌 The ONE spelling of a window's cache key — the saga writes these buckets and
// app/saga reads them; three inline templates was three chances to drift.
export const logsWindowCacheKey = (scopeKey, days) => `${scopeKey}:${days}`;

const cacheKey = logsWindowCacheKey;

//📌 Every access log in the window '<scopeKey>:<days>' for the active org — the
// one source the portfolio cards and the Lockers dashboard share. Returns [].
export const selectLogsWindow = createSelector(
	[selectWindows, selectOrgID, (_, scopeKey) => scopeKey, (_, __, days) => days],
	(logsWindow, orgID, scopeKey, days) => logsWindow?.[orgID]?.[cacheKey(scopeKey, days)]?.logs || []
);

//📌 Whether the window '<scopeKey>:<days>' has been fetched at least once, so a card
// shows a spinner instead of a blank state while a new range loads.
export const selectLogsWindowReady = createSelector(
	[selectWindows, selectOrgID, (_, scopeKey) => scopeKey, (_, __, days) => days],
	(logsWindow, orgID, scopeKey, days) => Boolean(logsWindow?.[orgID]?.[cacheKey(scopeKey, days)])
);

//📌 The server's total for the window — what `logs` WOULD hold had the page cap
// not applied. Equals logs.length on any untruncated window.
export const selectLogsWindowTotal = createSelector(
	[selectWindows, selectOrgID, (_, scopeKey) => scopeKey, (_, __, days) => days],
	(logsWindow, orgID, scopeKey, days) => logsWindow?.[orgID]?.[cacheKey(scopeKey, days)]?.total ?? 0
);

//📌 True when the page cap cut the window short, so `logs` is a recent prefix.
// Any count derived from it is then a floor — say "sampled", not a total.
export const selectLogsWindowTruncated = createSelector(
	[selectWindows, selectOrgID, (_, scopeKey) => scopeKey, (_, __, days) => days],
	(logsWindow, orgID, scopeKey, days) => Boolean(logsWindow?.[orgID]?.[cacheKey(scopeKey, days)]?.truncated)
);
